<template>
  <div>
    <div class="panel m-3">
      <PanelHeader :panel-header="factsLine || 'Sozialarbeiter'" />
      <div class="container pt-3 pb-3">
        <div
          v-if="socialWorker"
          class="stammdaten"
        >
          <div class="stammdaten-facts">
            <p v-if="!hasContactData" class="has-text-grey">
              Keine weiteren Informationen erfasst.
            </p>
            <template v-if="hasContactData">
              <div>
                <p v-if="email">
                  {{ email }}
                </p>
                <p v-if="phone1Line">
                  {{ phone1Line }}
                </p>
                <p v-if="phone2Line">
                  {{ phone2Line }}
                </p>
                <p v-if="addressLine">
                  {{ addressLine }}
                </p>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="relatedClients.length > 0"
      class="panel m-3"
    >
      <p class="panel-heading">
        Klienten
      </p>
      <div class="container pt-3 pb-3">
        <b-table
          class="member-table"
          :data="relatedClients"
          narrowed
          striped
          hoverable
          @click="row => openClient(row.client)"
        >
          <b-table-column
            v-slot="props"
            label="Name"
            cell-class="has-text-left"
          >
            {{ props.row.client.attributes.first_name }}
            {{ props.row.client.attributes.last_name }}
          </b-table-column>
          <b-table-column
            v-slot="props"
            label="Gruppe"
            cell-class="has-text-left"
          >
            {{ props.row.client.attributes.group
              && props.row.client.attributes.group.attributes
              ? props.row.client.attributes.group.attributes.group_name
              : "" }}
          </b-table-column>
          <b-table-column
            v-slot="props"
            label="Zugang"
            cell-class="has-text-left"
          >
            {{ props.row.entry
              ? props.row.entry.toLocaleDateString("de-DE") : "-" }}
          </b-table-column>
          <b-table-column
            v-slot="props"
            label="Abgang"
            cell-class="has-text-left"
          >
            {{ props.row.exit
              ? props.row.exit.toLocaleDateString("de-DE") : "-" }}
          </b-table-column>
          <b-table-column
            v-slot="props"
            label="Aktivität"
            cell-class="has-text-left"
          >
            {{ props.row.active ? "Aktiv" : "Inaktiv" }}
          </b-table-column>
        </b-table>
      </div>
    </div>

    <div
      v-if="relatedPersons.length > 0"
      class="panel m-3"
    >
      <p class="panel-heading">
        Weitere Personen
      </p>
      <div class="container pt-3 pb-3">
        <b-table
          class="member-table"
          :data="relatedPersons"
          narrowed
          striped
          hoverable
          @click="openPerson"
        >
          <b-table-column
            v-slot="props"
            label="Name"
            cell-class="has-text-left"
          >
            {{ props.row.attributes.first_name }}
            {{ props.row.attributes.last_name }}
          </b-table-column>
          <b-table-column
            v-slot="props"
            label="Art"
            cell-class="has-text-left"
          >
            {{ formatType(props.row.attributes.type) }}
          </b-table-column>
        </b-table>
      </div>
    </div>

    <section class="mx-5 my-2">
      <div
        class="buttons"
        style="display: flex"
      >
        <b-button
          class="is-primary"
          tag="router-link"
          to="/socialWorker"
        >
          Zurück
        </b-button>
        <b-button
          class="is-danger"
          style="margin-left: auto"
          @click="isDeleteCheck = true"
        >
          Löschen
        </b-button>
        <b-button
          class="is-primary"
          @click="triggerEdit"
        >
          Bearbeiten
        </b-button>
      </div>
    </section>
    <ModalComponent
      v-if="isDeleteCheck && socialWorker"
      :item="getName(socialWorker)"
      :is-delete-check="isDeleteCheck"
      :is-loading="isLoading"
      :cancel="() => isDeleteCheck = false"
      :submit="submitDelete"
    />
  </div>
</template>

<script>
import Parse from "parse/dist/parse.min.js";
import PanelHeader from "@/components/helperComponents/PanelHeader";
import ModalComponent from "@/components/helperComponents/ModalComponent";
import { positiveFeedBack } from "@/lib/Toast";
import { mapGetters } from "vuex";
export default {
	name: "ViewSocialWorker",
	components: {ModalComponent, PanelHeader},
	data(){
		return{
			socialWorker: undefined,
			isDeleteCheck: false,
			isLoading: false
		}
	},
	computed: {
		...mapGetters({
			clients: "getClients"
		}),
		factsLine() {
			if (!this.socialWorker) return "";
			const a = this.socialWorker.attributes;
			const name = [ a.first_name, a.last_name ]
				.filter(Boolean).map(s => s.trim()).join(" ");
			return [ name, "Sozialarbeiter" ].join(", ");
		},
		email() {
			const email = this.socialWorker && this.socialWorker.attributes.email;
			return email ? email.trim() : "";
		},
		phone1Line() {
			if (!this.socialWorker) return "";
			const a = this.socialWorker.attributes;
			return this.formatPhone(a.phone1, a.info1);
		},
		phone2Line() {
			if (!this.socialWorker) return "";
			const a = this.socialWorker.attributes;
			return this.formatPhone(a.phone2, a.info2);
		},
		addressLine() {
			if (!this.socialWorker) return "";
			const a = this.socialWorker.attributes;
			const city = [ a.plz, a.location ]
				.filter(Boolean).map(s => s.trim()).filter(Boolean).join(" ");
			const street = a.street ? a.street.trim() : "";
			return [ street, city ].filter(Boolean).join(", ");
		},
		hasContactData() {
			return !!(this.email || this.phone1Line || this.phone2Line ||
				this.addressLine);
		},
		/**
		 * All client case records having this social worker. Derived from
		 * the store, so no extra requests are needed.
		 */
		relatedClientRecords() {
			if (!this.socialWorker || !this.clients) return [];
			return this.clients.filter(client => {
				const assigned = client.attributes.socialWorker;
				return !!(assigned && assigned.id === this.socialWorker.id);
			});
		},
		/**
		 * One row per child: case records deduplicated by their uuid.
		 * Activity and the current entry/exit dates are taken from the
		 * child's active case record, or the most recent one when all
		 * cases are inactive.
		 */
		relatedClients() {
			const seen = new Set();
			const rows = [];
			for (const record of this.relatedClientRecords) {
				const uuid = record.attributes.uuid || record.id;
				if (seen.has(uuid)) continue;
				seen.add(uuid);
				const cases = (this.clients || []).filter(c =>
					(c.attributes.uuid || c.id) === uuid);
				const active = cases.filter(c => !c.attributes.inactive);
				const current = active[0] || cases.slice().sort((a, b) =>
					(b.attributes.entry || 0) - (a.attributes.entry || 0))[0] ||
					record;
				rows.push({
					client: record,
					active: active.length > 0,
					entry: current.attributes.entry,
					exit: current.attributes.exit
				});
			}
			return rows;
		},
		/** Persons attached to the same clients. */
		relatedPersons() {
			const seen = new Set([ this.socialWorker ? this.socialWorker.id : "" ]);
			const persons = [];
			for (const client of this.relatedClientRecords) {
				for (const person of client.attributes.persons || []) {
					if (!person || seen.has(person.id)) continue;
					if (!person.attributes || !person.attributes.first_name) {
						continue;
					}
					seen.add(person.id);
					persons.push(person);
				}
			}
			return persons;
		}
	},
	async mounted() {
		this.socialWorker = this.$route.params.data;
		// Reached via browser navigation (back/forward, reload, deep
		// link): the in-memory object is gone, load it by the URL id.
		if (!this.socialWorker && this.$route.params.id) {
			try {
				this.socialWorker =
					await new Parse.Query("Person").get(this.$route.params.id);
			}
			catch (e) {
				console.error(e);
			}
		}
		if (!this.socialWorker) {
			return await this.$router.push({path: "/socialWorker"});
		}
		this.$store.dispatch("setDetailTitle", this.getName(this.socialWorker));
	},
	methods:{
		formatPhone(phone, info) {
			phone = phone ? phone.trim() : "";
			info = info ? info.trim() : "";
			if (!phone) return "";
			return info ? `${phone} (${info})` : phone;
		},
		formatType(type) {
			return type === "socialWorker" ? "Sozialarbeiter" : type;
		},
		guardSelection() {
			// A click that ends a drag-selection selects text, it should
			// not navigate.
			const selection = window.getSelection && window.getSelection();
			return !!(selection && selection.toString().length > 0);
		},
		openClient(client) {
			if (this.guardSelection()) return;
			this.$router.push(
				{ name: "ViewClient", params: { id: client.id, data: client } });
		},
		openPerson(person) {
			if (this.guardSelection()) return;
			const name = person.attributes.type === "socialWorker"
				? "ViewSocialWorker"
				: "ViewPerson";
			this.$router.push({ name, params: { id: person.id, data: person } });
		},
		triggerEdit(){
			this.$router.push(
				{ name: "AddSocialWorker", params: { data: this.socialWorker } });
		},
		async submitDelete(){
			this.isLoading = true;
			const name = this.getName(this.socialWorker);
			this.socialWorker.set("deleted", true);
			await this.socialWorker.save();
			this.$store.dispatch("socialWorkers/delete", this.socialWorker);
			this.isDeleteCheck = false;
			this.isLoading = false;
			await this.$router.push({path: "/socialWorker"});
			await positiveFeedBack(name + " gelöscht!");
		},
		getName(socialWorker){
			return `${socialWorker.attributes.first_name} ${socialWorker.attributes.last_name}`
		}
	}
}
</script>

<style scoped>
.stammdaten {
	display: flex;
	align-items: flex-start;
	gap: 1.5rem;
	padding-left: 1.5rem;
}

.stammdaten-facts {
	text-align: left;
	min-width: 0;
}

.stammdaten-facts p {
	margin-bottom: 0.25rem;
}

.member-table >>> tbody tr {
	cursor: pointer;
}

@media screen and (max-width: 768px) {
	.member-table {
		padding: 0 0.75rem;
	}
}
</style>
